import React, { useState } from 'react';
import { XCircle, X, AlertCircle } from 'lucide-react';
import Button from '../../../components/ui/Button';
import { registrationService } from '../../../services/registrationService';
import { useAuth } from '../../../contexts/AuthContext';

const RejectionModal = ({ registrationId, schoolName, currentStatus, onClose, onSuccess }) => {
  const [reason, setReason] = useState('');
  const [selectedReasons, setSelectedReasons] = useState([]);
  const [sendNotification, setSendNotification] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const { user } = useAuth();

  const predefinedReasons = [
    'Incomplete documentation',
    'Invalid player information',
    'Age eligibility issues',
    'Missing required certificates',
    'Insufficient player count',
    'Invalid MCA IDs',
    'Document quality issues',
    'Non-compliant with tournament rules'
  ];

  const toggleReason = (reasonText) => {
    setSelectedReasons(prev =>
      prev?.includes(reasonText)
        ? prev?.filter(r => r !== reasonText)
        : [...prev, reasonText]
    );
  };

  const handleReject = async () => {
    if (selectedReasons?.length === 0 && !reason?.trim()) {
      setError('Please select at least one reason or provide custom feedback');
      return;
    }

    if (!registrationId || !user?.id) {
      setError('Missing registration ID or user authentication');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      // Combine selected reasons and custom reason
      const rejectionReason = [
        ...selectedReasons,
        reason?.trim()
      ].filter(Boolean).join('; ');

      await registrationService.rejectRegistration(registrationId, user.id, rejectionReason, sendNotification);
      
      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (err) {
      console.error('Error rejecting registration:', err);
      setError(err?.message || 'Failed to reject registration. Please try again.');
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 sticky top-0 bg-white">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 rounded-lg">
              <XCircle className="w-6 h-6 text-red-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-900">Reject Registration</h3>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="flex items-start gap-3 p-4 bg-yellow-50 border border-yellow-200 rounded-lg mb-6">
            <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm text-yellow-800">
                You are about to reject the registration for{' '}
                <span className="font-semibold">{schoolName}</span>.
                Please provide clear reasons to help the school understand what needs to be corrected.
              </p>
            </div>
          </div>

          {/* Predefined Reasons */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Select Rejection Reasons
            </label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {predefinedReasons?.map((reasonText) => (
                <button
                  key={reasonText}
                  onClick={() => toggleReason(reasonText)}
                  className={`text-left px-4 py-3 rounded-lg border-2 transition-all ${
                    selectedReasons?.includes(reasonText)
                      ? 'border-red-500 bg-red-50 text-red-900' :'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <div
                      className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                        selectedReasons?.includes(reasonText)
                          ? 'border-red-500 bg-red-500' :'border-gray-300'
                      }`}
                    >
                      {selectedReasons?.includes(reasonText) && (
                        <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      )}
                    </div>
                    <span className="text-sm font-medium">{reasonText}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Custom Reason */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Additional Details (Optional)
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e?.target?.value)}
              placeholder="Provide specific details about the rejection..."
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
              rows="4"
            />
          </div>

          {/* Notification Checkbox */}
          <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg mb-6">
            <input
              type="checkbox"
              id="sendNotification"
              checked={sendNotification}
              onChange={(e) => setSendNotification(e?.target?.checked)}
              className="mt-1 w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-red-500"
            />
            <label htmlFor="sendNotification" className="text-sm text-gray-700">
              Send rejection notification to school with detailed feedback via email and SMS
            </label>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3">
            <Button
              onClick={onClose}
              disabled={isSubmitting}
              className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-2.5 rounded-lg font-medium disabled:opacity-50"
            >
              Cancel
            </Button>
            <Button
              onClick={handleReject}
              disabled={isSubmitting || (selectedReasons?.length === 0 && !reason?.trim())}
              className="flex-1 bg-red-600 hover:bg-red-700 text-white px-4 py-2.5 rounded-lg font-medium flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {isSubmitting ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Rejecting...
                </>
              ) : (
                <>
                  <XCircle className="w-5 h-5" />
                  Reject Registration
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RejectionModal;